/**
 * DeviceDrawer Styled Components
 * 
 * Provides styled components for the device drawer interface
 * with Ford theme integration and responsive design - matching PlugDrawer styles
 */

import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Drawer,
  Chip
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY } from '../../theme/fordTheme.js';

// Common style constants to reduce duplication
const COMMON_STYLES = {
  borderColor: '#233058',
  borderRadius: '12px',
  padding: '20px',
  fontSize: {
    small: '0.875rem',
    medium: '1.125rem',
    tiny: '12px'
  },
  colors: {
    success: {
      bg: 'rgba(34, 197, 94, 0.2)',
      color: '#22c55e',
      border: '1px solid rgba(34, 197, 94, 0.3)'
    },
    warning: {
      bg: 'rgba(251, 191, 36, 0.2)',
      color: '#fbbf24',
      border: '1px solid rgba(251, 191, 36, 0.3)'
    },
    error: {
      bg: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.3)'
    },
    default: {
      bg: 'rgba(156, 163, 175, 0.2)',
      color: '#9ca3af',
      border: '1px solid rgba(156, 163, 175, 0.3)'
    }
  }
};

// Helper function to get chip styles
const getChipStylesByType = (chiptype) => {
  switch (chiptype) {
    case 'chip-ok':
      return COMMON_STYLES.colors.success;
    case 'chip-warn':
      return COMMON_STYLES.colors.warning;
    case 'chip-error':
      return COMMON_STYLES.colors.error;
    default:
      return COMMON_STYLES.colors.default;
  }
};

// Main drawer component
export const DeviceDrawerContainer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '650px',
    maxWidth: '90vw',
    backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textMuted,
    border: `1px solid ${COMMON_STYLES.borderColor}`,
    borderRadius: '6px',
  },
}));


// Drawer content area
export const DeviceDrawerContent = styled(Box)(({ theme }) => ({
  padding: COMMON_STYLES.padding,
  height: '100%',
  overflowY: 'auto',
  backgroundColor: FORD_COLORS.backgroundford,
}));



// Content panel for alerts
export const DeviceDrawerTabContentPanel = styled(Box)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  borderRadius: COMMON_STYLES.borderRadius,
  padding: COMMON_STYLES.padding,
   border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
}));

// Tab content header - matching OfflineEventsDrawer typography
export const DeviceDrawerTabContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& h4': {
    color: 'rgb(19, 34, 52)', // Match OfflineEventsDrawer dark text
    margin: 0,
    fontSize: '16px', // Consistent sizing
    fontWeight: 600,
    fontFamily: FORD_TYPOGRAPHY.newfont,
  },
}));

// Table wrapper - matching OfflineEventsDrawer pattern
export const DeviceDrawerTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  border: '1px solid #233058',
  // borderRadius: '8px',
  // Hide scrollbar while maintaining scroll functionality
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Edge
  },
  msOverflowStyle: 'none', // Internet Explorer 10+
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: FORD_TYPOGRAPHY.newfont, // Match OfflineEventsDrawer
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      '& th': {
        backgroundColor: 'rgb(10, 37, 64)', // Match OfflineEventsDrawer - Dark navy blue
        color: '#ffffff', // Match OfflineEventsDrawer - White text
        fontWeight: 700, // Match OfflineEventsDrawer - Bold weight
        fontSize: '12px', // Match OfflineEventsDrawer - Smaller font
        fontFamily: FORD_TYPOGRAPHY.newfont, // Match OfflineEventsDrawer
        padding: '4px', // Match OfflineEventsDrawer - Reduced padding
        textAlign: 'left',
        textTransform: 'uppercase', // Match OfflineEventsDrawer - Capital letters
        letterSpacing: '0.5px', // Match OfflineEventsDrawer - Better readability
        borderBottom: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 11,
      },
    },
    '& tbody': {
      position: 'relative',
      zIndex: 1,
      '& tr:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.05)', // Match OfflineEventsDrawer hover
        cursor: 'pointer',
      },
      '& td': {
        color: FORD_COLORS.textMuted, // Match OfflineEventsDrawer text color
        padding: '4px', // Match OfflineEventsDrawer padding
        textAlign: 'left',
        fontSize: '12px', // Match OfflineEventsDrawer font size
        fontFamily: FORD_TYPOGRAPHY.newfont, // Match OfflineEventsDrawer font
        borderBottom: '1px solid rgba(35, 48, 88, 0.5)',
        backgroundColor: FORD_COLORS.backgroundford,
      },
    },
  },
}));

// Status chip
export const DeviceDrawerStatusChip = styled(Chip)(({ theme, chiptype }) => ({
  fontSize: COMMON_STYLES.fontSize.tiny,
  height: '24px',
  fontWeight: 500,
  backgroundColor: getChipStylesByType(chiptype).bg,
  color: getChipStylesByType(chiptype).color,
  border: getChipStylesByType(chiptype).border,
}));

// Loading message
export const DeviceDrawerLoadingMessage = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9aa3c7',
  fontSize: '14px',
  padding: COMMON_STYLES.padding,
}));


// Error message box
export const DeviceDrawerErrorBox = styled(Box)(({ theme }) => ({
  backgroundColor: COMMON_STYLES.colors.error.bg,
  border: COMMON_STYLES.colors.error.border,
  borderRadius: COMMON_STYLES.borderRadius,
  padding: '16px',
  marginBottom: COMMON_STYLES.padding,
}));

// Sort icon styles
export const DeviceDrawerSortIcon = styled(Box)(({ theme }) => ({
  fontSize: '16px',
  '&.inactive': {
    opacity: 0.5
  }
}));

// Close icon styles
export const DeviceDrawerCloseIcon = styled(Box)(({ theme }) => ({
  marginRight: '8px',
  fontSize: '18px'
}));

// Loading spinner
export const DeviceDrawerLoadingSpinner = styled(Box)(({ theme }) => ({
  marginRight: '8px'
}));

// Alert container
export const DeviceDrawerAlertContainer = styled(Box)(({ theme }) => ({
  width: '100%'
}));

// Empty state cell
export const DeviceDrawerEmptyStateCell = styled('td')(({ theme }) => ({
  textAlign: 'center',
  padding: COMMON_STYLES.padding
}));

// Device detail row styling - matching OfflineEventsDrawer pattern
export const DeviceDetailRowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '1px 0', // Add some vertical padding
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& .detail-label': {
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'rgb(138, 152, 166)',
    fontSize: '12px',
    letterSpacing: '0.5px',
    lineHeight: 1.2,
    marginBottom: '2px',
  },
  '& .detail-value': {
    color: 'rgb(19, 34, 52)',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.3,
  },
}));

